// src/lib/opportunity/productTypeLabel.ts
//
// Resolves an `opp_opportunities.product_type` tag into a human-readable
// label from Ops Deal Types. Callers pass `useDualReadProductTypes()`.
// Falls back to the raw tag if the catalog has no label.
//
// The well-known "other" sentinel renders as "Other" so existing rows
// captured before this slice keep displaying cleanly.
//
// Pure function, no React deps — pass the dual-read catalog entries.

import {
  OPPORTUNITY_PRODUCT_TYPE_OTHER,
  OPPORTUNITY_PRODUCT_TYPE_OTHER_LABEL,
} from "@/types/opportunity";
import type { ProductTypeEntry } from "@/hooks/useOrgSettings";
import type { EffectiveServiceOffering } from "@/lib/service/types";

export type OpportunityProductTypeOption = {
  value: string;
  label: string;
  publisherOrgName?: string;
  isInherited?: boolean;
};

export function formatOpportunityProductType(
  tag: string | null | undefined,
  productTypes: ReadonlyArray<ProductTypeEntry>,
): string {
  if (!tag) return OPPORTUNITY_PRODUCT_TYPE_OTHER_LABEL;
  const match = productTypes.find((p) => p.tag === tag);
  if (match) return match.label;
  if (tag === OPPORTUNITY_PRODUCT_TYPE_OTHER) return OPPORTUNITY_PRODUCT_TYPE_OTHER_LABEL;
  return tag;
}

/**
 * Returns the dropdown option list for the New-Opportunity modal: Deal
 * Types, inherited quotable service offerings (when resale is enabled),
 * plus the "Other" sentinel appended at the end.
 */
export function buildOpportunityProductTypeOptions(
  productTypes: ReadonlyArray<ProductTypeEntry>,
  inheritedOfferings: ReadonlyArray<EffectiveServiceOffering> = [],
): OpportunityProductTypeOption[] {
  const ownTags = new Set(productTypes.map((p) => p.tag));
  const inheritedOptions: OpportunityProductTypeOption[] = inheritedOfferings
    .filter((o) => o.is_inherited && o.quotable_from_sales && o.is_active)
    .filter((o) => !ownTags.has(o.slug))
    .map((o) => ({
      value: o.slug,
      label: o.label,
      publisherOrgName: o.publisher_org_name,
      isInherited: true,
    }));

  return [
    ...productTypes.map((p) => ({ value: p.tag, label: p.label })),
    ...inheritedOptions,
    { value: OPPORTUNITY_PRODUCT_TYPE_OTHER, label: OPPORTUNITY_PRODUCT_TYPE_OTHER_LABEL },
  ];
}

export function formatOpportunityProductTypeOptionLabel(
  option: OpportunityProductTypeOption,
): string {
  if (option.isInherited && option.publisherOrgName) {
    return `${option.label} (${option.publisherOrgName})`;
  }
  return option.label;
}
